import { Dropdown, MenuProps, Spin, Tooltip } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { useState, useMemo } from 'react';
import { User } from 'server/src/types/office-service-type';
import { trpc, trpcClient } from '../../trpc';
import { userStatusCustomOption, userStatusOptions } from './utils/userStatus';

interface UserAvatarProps {
  name?: string;
  positionX?: number;
  positionY?: number;
  avatarImage?: string;
  avatarWidth?: number;
  avatarHeight?: number;
  userId: number;
  userStatus: string;
  onGetPrivateRoomUrl: (url: string) => void;
}

const items: MenuProps['items'] = [{ label: 'メッセージ', key: '1' }];

function UserAvatar({
  name,
  positionX,
  positionY,
  avatarImage,
  avatarWidth,
  avatarHeight,
  userId,
  userStatus,
  onGetPrivateRoomUrl
}: UserAvatarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const currentUserRoot = useMemo<User>(() => {
    return jwtDecode(new URLSearchParams(window.location.search).get('accessToken') ?? '');
  }, []); // Empty dependency array means it only runs once
  const getPrivateRoomUrl = trpc.PrivateRoomUSI.getPrivateRoomUrl.useMutation();

  let employeeColorBG;
  let employeeColorText;

  if (currentUserRoot.employmentType === 'FULLTIME') {
    employeeColorBG = '#009900';
    employeeColorText = '#ffffff';
  } else if (currentUserRoot.employmentType === 'CONTRACT') {
    employeeColorBG = '#99FFFF';
    employeeColorText = '#222222';
  } else if (currentUserRoot.employmentType === 'TEMPORARY') {
    employeeColorBG = '#99FF66';
    employeeColorText = '#222222';
  } else if (currentUserRoot.employmentType === 'PARTTIME') {
    employeeColorBG = '#33CCFF';
    employeeColorText = '#222222';
  } else if (currentUserRoot.employmentType === 'OFFICER') {
    employeeColorBG = '#FFFFFF';
    employeeColorText = '#222222';
  }

  const handleMenuClick = () => {
    if (currentUserRoot.id !== userId) {
      return;
    }
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === items?.[0]?.key) {
      getPrivateRoomUrl.mutate(
        { userIds: [currentUserRoot.id, userId] },
        {
          onSuccess: (data) => {
            onGetPrivateRoomUrl(data);
          },
          onError: (error) => {
            console.error('Error when get private room url:', error);
          }
        }
      );
    }

    setMenuOpen(false);
  };

  const currentUserStatus =
    userStatusOptions.find((option) => option.value === (userStatus || '')) || userStatusCustomOption;
  return (
    <Dropdown
      menu={{ items, onClick: handleMenuItemClick }}
      trigger={['click']}
      open={currentUserRoot.id !== userId && menuOpen}
      onOpenChange={(open) => setMenuOpen(open)}
    >
      <Tooltip
        color={currentUserStatus.hoverColor}
        title={userStatus || 'ステータスなし'}
        overlayStyle={{
          zIndex: 100,
          fontSize: '14px',
          padding: '0px',
          margin: '0px'
        }}
      >
        <div
          data-name={`${name}`}
          className={`absolute rounded-full border-[var(--status-color)] transition-all duration-200  border-[20px]
          ${
            currentUserRoot.id !== userId
              ? 'hover:border-[20px] hover:border-[var(--status-hover-color)] cursor-pointer hover:shadow-[0_0_20px_5px_var(--status-hover-color)]'
              : ''
          }
          before:content-[attr(data-name)] before:absolute before:bottom-[-60px]
          before:left-1/2 before:text-[var(--employee-color-text)] before:whitespace-nowrap before:bg-[var(--employee-color-bg)]
          before:rounded-md before:transform before:-translate-x-1/2 before:text-center before:px-2 z-50`}
          style={{
            transform: `translate(${positionX}px, ${positionY}px)`,
            backgroundImage: `${avatarImage}`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            borderWidth: '8px',
            backgroundPosition: 'left top',
            fontSize: `${24}px`,
            height: `${avatarHeight}px`,
            width: `${avatarWidth}px`,
            ...({
              '--status-color': currentUserStatus.color,
              '--status-hover-color': currentUserStatus.hoverColor,
              '--employee-color-bg': employeeColorBG,
              '--employee-color-text': employeeColorText
            } as React.CSSProperties)
          }}
          onClick={handleMenuClick}
        />
      </Tooltip>
    </Dropdown>
  );
}

export default UserAvatar;
